import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { Search, PencilSquare, Trash, PlusCircle, Backspace, Diagram3, Receipt, CashCoin, HandThumbsUp } from "react-bootstrap-icons" // Iconos de Bootstrap

import { AlertError, AlertLoading } from '../helpers/AlertHelper'
import formatCurrency from "../helpers/currencyHelper"

import FormModal from "../components/modals/FormModal"
import TableManagement from "../components/tables/TableManagement"
import SearchSelect from '../components/form/SearchSelect'

import PaymentsPartnersService from "../services/PaymentsPartnersService"
import ProjectService from "../services/ProjectsService"
import PartnersService from "../services/PartnersService"

import PaymentsPartnersForm from "./PaymentsPartnersForm"
import PaymentsPartnersExecutedForm from "./PaymentsPartnersExecutedForm"

const PaymentsPartners = () => {
    const intl = useIntl()
    const [paymentPartnerId, setPaymentPartnerId] = useState(null)
    const [paymentsPartners, setPaymentsPartners] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("projectId")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    const [projectsList, setProjectsList] = useState([])
    const [selectedProject, setSelectedProject] = useState('')
    const [partnersList, setPartnersList] = useState([])
    const [selectedPartner, setSelectedPartner] = useState('')

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch projects
    const fetchProjects = async () => {
        try {
            const response = await ProjectService.getProjects(1, '', 2000)
            setProjectsList(response.data.projects)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch partners
    const fetchPartners = async () => {
        try {
            const response = await PartnersService.getPartners(1, '', 2000)
            setPartnersList(response.data.partners)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch payments to partners
    const fetchPaymentsPartners = async () => {
        try {
            const response = await PaymentsPartnersService.getPaymentsPartners(currPage, search, perPage, sortBy, sortOrder, selectedProject, selectedPartner)
            const summedPartnersPayments = response.data.paymentsPartners.map((partner) => {
                // Sumar todos los amounts en payments
                const totalPayments = partner.payments.reduce(
                    (sum, payment) => sum + payment.amount,
                    0
                )

                return {
                    ...partner,
                    totalPayments: totalPayments, // Para limitar a dos decimales
                }
            })
            setPaymentsPartners(summedPartnersPayments)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }



    const handleModal = () => {
        setModalTrigger(false)
        fetchPaymentsPartners()
    }

    const handleSelectProjectChange = (e) => {
        setSelectedProject(e.target.value)
        setCurrPage(1)
    }

    const handleSelectPartnerChange = (e) => {
        setSelectedPartner(e.target.value)
        setCurrPage(1)
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setSelectedProject('')
        setSelectedPartner('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // fetch projects when start
    useEffect(() => {
        fetchProjects()
        fetchPartners()
    }, [])

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchPaymentsPartners()
    }, [currPage, perPage, search, sortBy, sortOrder, selectedProject, selectedPartner])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="paymentsAdd" defaultMessage="Add payment" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="paymentsEdit" defaultMessage="Edit payment" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="paymentsDelete" defaultMessage="Delete payment" />,
                icon: <Trash />,
            },
            payments: {
                title: <FormattedMessage id="paymentsExecuted" defaultMessage="Payments executed" />,
                icon: <CashCoin />,
            }
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setPaymentPartnerId(type === 'add' ? false : data._id) // Si 'add', incomeid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'projectId',
            name: <FormattedMessage id="paymentsProject" defaultMessage="Project" />,
            selector: (row) => row.projectId.name,
            sortable: true,
        },
        {
            id: 'partnerId',
            name: <FormattedMessage id="paymentsPartner" defaultMessage="Partner" />,
            selector: (row) => row.partnerId.name,
            sortable: true,
        },
        {
            id: 'concept',
            name: <FormattedMessage id="paymentsConcept" defaultMessage="Concept" />,
            selector: (row) => row.concept,
        },
        {
            id: 'amount',
            name: <FormattedMessage id="paymentsAmount" defaultMessage="Amount" />,
            selector: (row) => formatCurrency(row.amount),
            sortable: true,
            right: "true"
        },
        {
            id: 'pending',
            name: <FormattedMessage id="paymentsPending" defaultMessage="Payments pending" />,
            selector: (row) => formatCurrency(row.amount - row.totalPayments),
            right: "true"
        },
        {
            id: 'paymentsBtns',
            name: <FormattedMessage id="paymentsExecuted" defaultMessage="Payments executed" />,
            cell: (row) => {
                return (
                    <Button onClick={() => handleButtonClick({ data: row, type: 'payments' })} variant="primary" size="sm">
                        <CashCoin /> {formatCurrency(row.totalPayments)}
                    </Button>
                )
            },
            right: "true",
            ignoreRowClick: true,
        },
    ]

    return (
        <Container fluid className="p-0">
            <Row className="p-1 bg-secondary">
                <Col>
                    <InputGroup>
                        <InputGroup.Text><Diagram3 /></InputGroup.Text>
                        <SearchSelect
                            fieldName={'projectId'}
                            selected={selectedProject}
                            onChange={handleSelectProjectChange}
                            options={projectsList}
                        />
                        <InputGroup.Text><HandThumbsUp /></InputGroup.Text>
                        <SearchSelect
                            fieldName={'partnerId'}
                            selected={selectedPartner}
                            onChange={handleSelectPartnerChange}
                            options={partnersList}
                        />
                        <InputGroup.Text><Search /></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search payments..." })}
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Button onClick={handleSearchClean} variant="light">
                            <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                        </Button>

                        <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                            <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {isLoaded ? (
                <TableManagement
                    icon={<Receipt />}
                    title={<FormattedMessage id="navPaymentsPartners" defaultMessage="Payments to partners" />}
                    columns={columns}
                    data={paymentsPartners}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={modalAction !== 'payments' ? PaymentsPartnersForm : PaymentsPartnersExecutedForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={paymentPartnerId}
                action={modalAction} />

        </Container>
    )
}

export default PaymentsPartners